import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiPlus, FiMinus, FiChevronDown, FiChevronUp, FiHome, FiVideo, FiMapPin, FiCalendar, FiClock } from 'react-icons/fi';
import {
  CLASS_LOCATION, formatClassDate, formatClassTime, formatCourseRange, formatMoney, formatShortDate,
  getCourseWeeks, getInitials, getSeasonLabel, hasCourseStarted, parseCourseDate
} from '../courseCatalogHelpers';
import styles from '../CourseCatalog.module.css';

const ACCENT_COUNT = 6;

// variant 'active': tinted banner (code, "Starts Oct 22", weeks and dates) above the body.
// variant 'previous': compact card with a coloured top edge and a season label.
export default function CourseCard({
  course,
  variant = 'active',
  index = 0,
  bundleOffer = null,
  isSelected,
  isEnrolled,
  isExpanded,
  onToggleSelection,
  onToggleExpansion
}) {
  const descRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const isActiveCard = variant === 'active';
  const clampLines = isActiveCard ? 3 : 2;
  const accentClass = styles[`accent${index % ACCENT_COUNT}`];

  useEffect(() => {
    const checkTruncation = () => {
      const element = descRef.current;
      if (!element) return;

      const previousLineClamp = element.style.webkitLineClamp;
      const previousOverflow = element.style.overflow;
      const previousDisplay = element.style.display;

      element.style.display = '-webkit-box';
      element.style.webkitLineClamp = String(clampLines);
      element.style.overflow = 'hidden';
      const collapsedHeight = element.clientHeight;

      element.style.webkitLineClamp = 'unset';
      element.style.overflow = 'visible';
      element.style.display = 'block';
      const fullHeight = element.scrollHeight;

      element.style.webkitLineClamp = previousLineClamp;
      element.style.overflow = previousOverflow;
      element.style.display = previousDisplay;

      setIsTruncated(fullHeight > collapsedHeight + 1);
    };

    const frameId = requestAnimationFrame(checkTruncation);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(checkTruncation);
    }

    return () => cancelAnimationFrame(frameId);
  }, [course.description, clampLines]);

  const startDate = parseCourseDate(course.startDate || course.start_date);
  const weeks = getCourseWeeks(course);
  const seats = course.maxStudents || course.max_students;
  const instructor = course.instructor || 'TBA';
  const hint = isActiveCard && bundleOffer && bundleOffer.eligibleIds.has(course.id)
    ? `or ${formatMoney(bundleOffer.eachWithBundle(course))} each with the bundle`
    : null;
  const cartLabel = isSelected ? 'Remove from Cart' : 'Add to Cart';
  const classDate = formatClassDate(course);
  const classTime = formatClassTime(course);

  return (
    <motion.div
      className={`${styles.catalogCard} ${isActiveCard ? styles.activeCard : styles.previousCard} ${accentClass} ${isSelected ? styles.selected : ''}`}
      initial={false}
    >
      {isActiveCard ? (
        <div className={styles.cardBanner}>
          <div className={styles.cardBannerTop}>
            <span className={styles.codeTag}>{course.code}</span>
            {startDate && (
              <span className={styles.startsPill}>
                {hasCourseStarted(course) ? 'In progress' : `Starts ${formatShortDate(startDate)}`}
              </span>
            )}
          </div>
          <div className={styles.cardBannerDates}>
            {[weeks, formatCourseRange(course)].filter(Boolean).join(' · ') || 'Dates to be announced'}
          </div>
        </div>
      ) : (
        <div className={styles.previousTop}>
          <span className={styles.codeTag}>{course.code}</span>
          <span className={styles.seasonLabel}>{getSeasonLabel(course)}</span>
        </div>
      )}

      <div className={styles.cardBody}>
        {!isEnrolled && !course.isPaidCourse && (
          <span className={styles.freeBadge}>Free</span>
        )}
        <h3 className={styles.cardTitle}>{course.name}</h3>
        <div className={styles.descriptionWrapper}>
          <p
            ref={descRef}
            className={`${styles.cardDescription} ${isActiveCard ? '' : styles.cardDescriptionCompact} ${isExpanded ? styles.expanded : ''}`}
          >
            {course.description || 'No description available.'}
          </p>
          {isTruncated && (
            <button
              className={styles.readMoreButton}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpansion(course.id);
              }}
              type="button"
            >
              {isExpanded ? (
                <>
                  Show less
                  <FiChevronUp size={13} />
                </>
              ) : (
                <>
                  Read full description
                  <FiChevronDown size={13} />
                </>
              )}
            </button>
          )}
        </div>

        <div className={styles.cardMeta}>
          <span className={styles.instructorChip}>
            <span className={styles.avatar} aria-hidden="true">{getInitials(instructor)}</span>
            {instructor}
          </span>
          {isActiveCard && (
            <span className={styles.seatsChip}>
              <FiUsers size={13} aria-hidden="true" />
              {seats ? `${seats} seats` : 'Unlimited seats'}
            </span>
          )}
          {/* Every course is offered both ways; there is no per-course delivery setting to read. */}
          {isActiveCard && (
            <>
              <span className={styles.seatsChip}>
                <FiHome size={13} aria-hidden="true" />
                In person
              </span>
              <span className={styles.seatsChip}>
                <FiVideo size={13} aria-hidden="true" />
                Virtual
              </span>
            </>
          )}
        </div>
      </div>

      {isActiveCard && (
        <div className={styles.classDetails}>
          <div className={styles.classDetailsItem}>
            <FiMapPin size={15} aria-hidden="true" />
            <div>
              <div className={styles.classDetailsLabel}>Location</div>
              <div className={styles.classDetailsValue}>
                {CLASS_LOCATION.name}
                {CLASS_LOCATION.lines.map((line) => <span key={line} className={styles.classDetailsLine}>{line}</span>)}
              </div>
            </div>
          </div>
          {(classDate || classTime) && (
            <div className={styles.classDetailsColumn}>
              {classDate && (
                <div className={styles.classDetailsItem}>
                  <FiCalendar size={15} aria-hidden="true" />
                  <div>
                    <div className={styles.classDetailsLabel}>Date</div>
                    <div className={styles.classDetailsValue}>{classDate}</div>
                  </div>
                </div>
              )}
              {classTime && (
                <div className={styles.classDetailsItem}>
                  <FiClock size={15} aria-hidden="true" />
                  <div>
                    <div className={styles.classDetailsLabel}>Time</div>
                    <div className={styles.classDetailsValue}>{classTime}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.cardFooter}>
        <div className={styles.priceInfo}>
          <span className={styles.price}>
            {course.isPaidCourse ? formatMoney(course.price) : 'Free'}
          </span>
          {hint && <span className={styles.priceHint}>{hint}</span>}
        </div>
        {isEnrolled ? (
          <div className={styles.enrolledBadge}>ENROLLED</div>
        ) : (
          <button
            className={`${styles.ctaButton} ${isActiveCard ? '' : styles.ctaButtonOutline} ${isSelected ? styles.selected : ''}`}
            disabled={isEnrolled}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelection(course);
            }}
          >
            {isSelected ? <FiMinus size={15} /> : <FiPlus size={15} />}
            <span className={styles.buttonText}>{cartLabel}</span>
          </button>
        )}
      </div>
    </motion.div>
  );
}
